import { useEffect, useRef } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { HScroll, cn } from "@atrix-analytics/ui";

export interface SubNavItem {
  to: string;
  label: string;
  soon?: boolean;
}

/**
 * Underlined section tabs as real links (each is its own URL). On narrow screens the row scrolls
 * sideways, and a fade on the clipped edge shows there is more.
 */
export function SubNav({ items, projectId, label }: { items: SubNavItem[]; projectId: string; label: string }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const activeRef = useRef<HTMLAnchorElement>(null);
  // Keep the current tab in view when the row is scrolled or the page opens on a later tab.
  // Braces matter: newer Chromium returns a promise from scrollIntoView, which React would call as a cleanup.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [pathname]);
  return (
    <nav aria-label={label} className="-mx-4 border-b border-border sm:mx-0">
      <HScroll viewportClassName="px-4 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <ul className="flex min-w-max gap-1">
          {items.map((item) => {
            const href = item.to.replace("$projectId", projectId);
            const active = pathname === href || pathname.startsWith(`${href}/`);
            return (
              <li key={item.to}>
                <Link
                  to={item.to as "/p/$projectId"}
                  params={{ projectId }}
                  ref={active ? activeRef : undefined}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "relative -mb-px flex h-10 items-center gap-1.5 border-b-2 px-3 text-sm transition-colors",
                    active ? "border-accent font-medium text-fg" : "border-transparent text-fg-muted hover:text-fg",
                  )}
                >
                  {item.label}
                  {item.soon && <span className="text-[10px] tracking-wide text-fg-faint uppercase">Soon</span>}
                </Link>
              </li>
            );
          })}
        </ul>
      </HScroll>
    </nav>
  );
}
